/**
 * Dashboard pulse — pure summarizers that surface buried features on home.
 *
 * The Dashboard's "pulse" row makes three otherwise-buried capabilities
 * discoverable at a glance:
 *   (a) customer health — the top few slipping accounts (rankCustomersByRisk),
 *   (b) goals — how many orchestrator runs are actively in flight,
 *   (c) connections — whether the AI worker + Slack notifications are live
 *       or running in the honest local simulation.
 *
 * Everything here is pure (no store reads, no env reads) so it is trivially
 * testable; the view passes in live data + the workerInfo()/slackInfo()
 * flags and renders whatever comes back.
 */

import {
  rankCustomersByRisk,
  type CustomerHealth,
  type HealthInput,
} from './health';
import type { OrchestratorRun, Person } from '../db/types';

/** One slipping customer, paired with the transparent health breakdown. */
export interface AtRiskEntry {
  person: Person;
  health: CustomerHealth;
}

/**
 * The top `limit` slipping customers, riskiest first. Healthy accounts are
 * excluded — this is the "who should I touch today" shortlist, not a full
 * ranking. Default limit is 3 (a compact dashboard card).
 */
export function topAtRiskCustomers(
  people: Person[],
  input: HealthInput & { limit?: number } = {},
): AtRiskEntry[] {
  const limit = input.limit ?? 3;
  return rankCustomersByRisk(people, input)
    .filter((r) => r.health.band !== 'healthy')
    .slice(0, Math.max(0, limit));
}

/** Orchestrator statuses that count as "active" — in-flight goal runs. */
const ACTIVE_RUN_STATUSES: ReadonlySet<OrchestratorRun['status']> = new Set([
  'running',
  'awaiting_approval',
]);

/** How many goal runs are currently in flight (running or gated). */
export function countActiveGoals(runs: OrchestratorRun[]): number {
  return runs.filter((r) => ACTIVE_RUN_STATUSES.has(r.status)).length;
}

/** Compact rollup for the Dashboard's Goals card — all from real runs. */
export interface GoalsSummary {
  /** Every run ever recorded (any status). 0 → honest "No goals yet". */
  totalRuns: number;
  /** Runs currently in flight (running or paused at an approval gate). */
  activeRuns: number;
  /** Distinct goals among the in-flight runs. */
  activeGoals: number;
  /**
   * The next step an in-flight run is waiting on — the most recently
   * updated run's first gated step, falling back to its first pending
   * step — as "<goal label> — <step description>". Null when nothing is
   * queued (no active runs, or every step already resolved).
   */
  nextMilestone: string | null;
}

/**
 * Summarize the orchestrator runs for the compact Goals card. PURE: no
 * store reads, no clock — the view passes in the live `subscribeRuns`
 * snapshot and renders whatever comes back.
 */
export function goalsSummary(runs: OrchestratorRun[]): GoalsSummary {
  const active = runs.filter((r) => ACTIVE_RUN_STATUSES.has(r.status));
  // Most recently touched run first — its next step is "the" next milestone.
  const sorted = [...active].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  let nextMilestone: string | null = null;
  for (const run of sorted) {
    const step =
      run.steps.find((s) => s.status === 'awaiting_approval') ??
      run.steps.find((s) => s.status === 'pending');
    if (step) {
      nextMilestone = `${run.goalLabel} — ${step.description}`;
      break;
    }
  }
  return {
    totalRuns: runs.length,
    activeRuns: active.length,
    activeGoals: new Set(active.map((r) => r.goalId)).size,
    nextMilestone,
  };
}

/** Honest connection labels for the AI worker + Slack notification seams. */
export interface ConnectionsStatus {
  /** e.g. "Claude connected" / "Claude simulated". */
  ai: string;
  aiConnected: boolean;
  /** e.g. "Notifications on" / "Notifications simulated". */
  slack: string;
  slackConnected: boolean;
}

/**
 * Summarize the integration seams for the Connections status line. Inputs
 * are the `configured` flags from workerInfo()/slackInfo(), kept as plain
 * booleans so this stays pure (no import.meta.env reads here).
 */
export function connectionsStatus(opts: {
  aiConfigured: boolean;
  slackConfigured: boolean;
}): ConnectionsStatus {
  return {
    ai: opts.aiConfigured ? 'Claude connected' : 'Claude simulated',
    aiConnected: opts.aiConfigured,
    slack: opts.slackConfigured ? 'Notifications on' : 'Notifications simulated',
    slackConnected: opts.slackConfigured,
  };
}
